<template>
  <Dialog v-model:open="isOpen">
    <template v-if="slots.trigger">
      <slot name="trigger" :open="openDialog" />
    </template>
    <DialogTrigger v-else-if="slots.default" as-child>
      <slot />
    </DialogTrigger>

    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {{ t("pages.organizations.cloneDialog.title") }}
        </DialogTitle>
        <DialogDescription>
          {{ t("pages.organizations.cloneDialog.description") }}
        </DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="space-y-2">
          <Label for="clone-name">
            {{ t("pages.organizations.cloneDialog.nameLabel") }}
          </Label>
          <Input
            id="clone-name"
            v-model="formData.name"
            :placeholder="t('pages.organizations.cloneDialog.namePlaceholder')"
            :disabled="isLoading"
            required
          />
          <p class="text-sm text-muted-foreground">
            {{ t("pages.organizations.cloneDialog.nameDescription") }}
          </p>
        </div>

        <DialogFooter class="gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="isOpen = false"
          >
            {{ t("pages.organizations.cloneDialog.cancelButton") }}
          </Button>
          <Button
            type="submit"
            :disabled="isLoading || !formData.name.trim()"
          >
            <LoadingIndicator
              v-if="isLoading"
              class="mr-2"
            />
            <Copy
              v-else
              class="w-4 h-4 mr-2"
            />
            {{ t("pages.organizations.cloneDialog.confirmButton") }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Copy } from "lucide-vue-next";
import { cloneOrganization, useApiOrganizations } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import type { ApiOrganization } from "~/server/api/organizations/index.get";

interface Props {
  organization: ApiOrganization | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ cloned: [organization: ApiOrganization] }>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();
const slots = useSlots();

const openDialog = () => {
  if (props.organization) {
    isOpen.value = true;
  }
};

defineExpose({
  open: openDialog,
});

const { refresh } = await useApiOrganizations();

const formData = reactive({
  name: "",
});

watch(isOpen, (open) => {
  if (open && props.organization) {
    formData.name = t("pages.organizations.cloneDialog.defaultName", {
      name: props.organization.name,
    });
  } else if (!open) {
    formData.name = "";
    isLoading.value = false;
  }
});

const onSubmit = async () => {
  if (!props.organization) return;
  const trimmedName = formData.name.trim();
  if (!trimmedName) return;

  isLoading.value = true;

  try {
    const cloned = await cloneOrganization(props.organization.id, {
      name: trimmedName,
    });

    successToast({
      title: t("pages.organizations.cloneDialog.success.title"),
      description: t("pages.organizations.cloneDialog.success.description", {
        name: cloned.name,
      }),
    });

    await refresh();
    emit("cloned", cloned);
    isOpen.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to clone organization:", error);
    errorToast({
      title: t("pages.organizations.cloneDialog.error.title"),
      description:
        error?.data?.statusMessage ||
        t("pages.organizations.cloneDialog.error.description"),
    });
  } finally {
    isLoading.value = false;
  }
};

</script>
